import { computed, onMounted, onUnmounted, ref, toValue, watch } from 'vue'

import {
  getLoadingStepTaskIconName,
  resolveTaskIntervalMs
} from '@/utils/onboarding-ai/loading-step-config'

export const useOnboardingAiLoadingStepTasks = ({ tasks, variantConfig, loading, onComplete }) => {
  const activeTaskIndex = ref(0)
  let taskTimeoutId = null

  const taskCount = computed(() => toValue(tasks).length)
  const taskIntervalMs = computed(() => resolveTaskIntervalMs(toValue(variantConfig)))
  const lastTaskIndex = computed(() => Math.max(taskCount.value - 1, 0))

  const taskIconNames = computed(() => {
    return toValue(tasks).map((_, index) =>
      getLoadingStepTaskIconName({
        taskIndex: index,
        activeTaskIndex: activeTaskIndex.value,
        taskCount: taskCount.value
      })
    )
  })

  const clearTaskTimeout = () => {
    if (taskTimeoutId) {
      clearTimeout(taskTimeoutId)
      taskTimeoutId = null
    }
  }

  const complete = () => {
    clearTaskTimeout()
    activeTaskIndex.value = taskCount.value
    onComplete?.()
  }

  const isOnLastTask = () => activeTaskIndex.value >= lastTaskIndex.value

  const tryCompleteLastTask = () => {
    if (!isOnLastTask()) {
      return
    }

    if (!toValue(loading)) {
      complete()
    }
  }

  const scheduleCurrentTask = () => {
    if (taskCount.value === 0) {
      complete()
      return
    }

    clearTaskTimeout()

    if (isOnLastTask()) {
      tryCompleteLastTask()
      return
    }

    taskTimeoutId = setTimeout(() => {
      activeTaskIndex.value += 1
      scheduleCurrentTask()
    }, taskIntervalMs.value)
  }

  watch(loading, (isLoading, wasLoading) => {
    if (wasLoading && !isLoading && isOnLastTask()) {
      complete()
    }
  })

  onMounted(() => {
    scheduleCurrentTask()
  })

  onUnmounted(() => {
    clearTaskTimeout()
  })

  return {
    activeTaskIndex,
    taskIconNames
  }
}
